<template>
  <div class="relative flex flex-col items-stretch grow">
    <div
      v-if="isFetching && !isFetchingNextPage"
      class="h-full flex items-center justify-center"
    >
      <LoadingSpinner variant="primary" size="lg" />
    </div>
    <State
      v-else-if="!query && !files.length"
      :type="filesState.type"
      :title="filesState.title"
      :text="filesState.text"
      class="self-center grow"
    />
    <ListboxRoot
      v-else
      :modelValue="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      by="id"
      multiple
      highlightOnHover
      class="w-full h-full"
    >
      <ListboxContent class="w-full h-full flex flex-col grow">
        <div
          v-if="items.length > 0"
          ref="parentRef"
          class="h-full overflow-y-auto"
        >
          <div
            class="relative w-full"
            :style="{
              height: `${totalSize}px`,
            }"
          >
            <div
              v-for="virtualItem in virtualItems"
              :key="virtualItem.index"
              class="absolute top-0 left-0 w-full"
              :style="{
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }"
            >
              <template v-if="virtualItem.index === items.length">
                <PassportFileSkeleton v-if="hasNextPage" ref="loadMore" />
              </template>
              <template v-else-if="virtualItem.index > items.length">
                <PassportFileSkeleton v-if="hasNextPage" />
              </template>
              <template v-else-if="items[virtualItem.index]?.isSeparator">
                <div class="w-full h-1.5 flex justify-center">
                  <Separator
                    class="self-center w-[54px] border border-primary"
                  />
                </div>
              </template>
              <template v-else>
                <PassportFile
                  :value="items[virtualItem.index]"
                  variant="select"
                  :file="items[virtualItem.index]"
                  :checked="
                    selectedFileIds.includes(items[virtualItem.index].id)
                  "
                />
              </template>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center grow">
          <State
            :type="filesSearchState.type"
            :title="filesSearchState.title"
            :text="filesSearchState.text(query)"
            class="self-center grow"
          />
        </div>
      </ListboxContent>
    </ListboxRoot>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useVirtualizer } from "@tanstack/vue-virtual";
import { useIntersectionObserver } from "@vueuse/core";
import { ListboxContent, ListboxRoot, Separator } from "radix-vue";
import { LoadingSpinner } from "@/SharedComponents/LoadingSpinner";
import {
  PassportFile,
  PassportFileSkeleton,
} from "@/SharedComponents/Passport";
import { State, useState } from "@/SharedComponents/State";
import { useFiles } from "@/Composables/useFiles";

defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  files: { type: Array, required: true },
  buildingId: { type: String, required: false },
  excludeIds: { type: Array, default: () => [] },
  query: { type: String, required: false },
});

const {
  isLoading,
  data,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  resetPageParams,
} = useFiles(
  props.buildingId,
  computed(() => props.query)
);

const files = computed(() => {
  if (isLoading.value) {
    return [];
  }
  return data.value.pages
    .flatMap((p) => p.data)
    .filter((f) => !props.excludeIds.includes(f.id));
});

const selectedFileIds = computed(() => props.modelValue.map((f) => f.id));

const unselectedFiles = computed(() => {
  if (selectedFileIds.value.length === 0) {
    return files.value;
  }
  return files.value.filter((f) => !selectedFileIds.value.includes(f.id));
});

const showSeparator = computed(() => {
  return props.modelValue.length > 0 && unselectedFiles.value.length > 0;
});

const items = computed(() => {
  if (showSeparator.value) {
    return [
      ...props.modelValue,
      { isSeparator: true },
      ...unselectedFiles.value,
    ];
  }
  return [...props.modelValue, ...unselectedFiles.value];
});

const parentRef = ref(null);

const FILE_HEIGHT = 40;
const SEPARATOR_HEIGHT = 6;

const virtualizerOptions = computed(() => {
  return {
    count: hasNextPage.value ? items.value.length + 3 : items.value.length,
    getScrollElement: () => parentRef.value,
    estimateSize: (i) => {
      return items.value[i]?.isSeparator ? SEPARATOR_HEIGHT : FILE_HEIGHT;
    },
    gap: 4,
    overscan: 5,
  };
});

const virtualizer = useVirtualizer(virtualizerOptions);

const virtualItems = computed(() => virtualizer.value.getVirtualItems());

const totalSize = computed(() => virtualizer.value.getTotalSize());

watch(
  () => selectedFileIds.value.length,
  () => {
    virtualizer.value.measure();
  }
);

const loadMore = ref(null);

const { stop } = useIntersectionObserver(loadMore, ([{ isIntersecting }]) => {
  if (isIntersecting && !isFetchingNextPage.value) {
    fetchNextPage();
  }
});

onUnmounted(() => {
  stop();
  resetPageParams();
});

const filesStateStore = useState("files");
const { state: filesState } = storeToRefs(filesStateStore);

const filesSearchStateStore = useState("files_search");
const { state: filesSearchState } = storeToRefs(filesSearchStateStore);

defineExpose({
  empty: computed(() => files.value.length === 0),
});
</script>
